import Axios from 'helpers/Interceptor';

// Types for the unified invoice system (matching backend)
export interface UnifiedInvoice {
    id: string;
    amount: number;
    currency: string;
    status: string;
    createdDate: string;
    dueDate: string | null;
    invoiceLink: string | null;
    
    // Entity information
    entityType: 'organization' | 'brand';
    entityId: number;
    entityName: string;
    organizationName?: string;
    associatedBrandId?: number | null;
    
    // Payment information
    paymentPlatform: 'stripe';
    paymentIntentId?: string;
    subscriptionId?: string;
    
    // Type and categorization
    type: 'subscription' | 'campaign' | 'task-pack' | 'mixed';
    description: string;
    
    // Campaign-specific data (if applicable)
    campaignId?: number;
    campaignName?: string;
    campaignPlaybook?: string;
}

export interface UserInvoiceResponse {
    invoices: UnifiedInvoice[];
    summary: {
        totalInvoices: number;
        totalOrganizations: number;
        totalBrands: number;
        totalAmount: number;
        currency: string;
    };
    pagination?: {
        limit: number;
        offset: number;
        hasMore: boolean;
        total: number;
    };
}

export interface InvoiceFilters {
    organizationId?: number;
    brandId?: number;
    startDate?: string;
    endDate?: string;
    minAmount?: number;
    maxAmount?: number;
    limit?: number;
    offset?: number;
}

/**
 * Get comprehensive invoices for the current user, specific organization, or specific brand
 * This replaces the old getUserInvoices_api and provides unified invoice data
 * @param filters Optional filters to apply to the invoice query (organizationId, brandId, dates, amounts)
 * @returns Promise<UserInvoiceResponse> Unified invoice response with summary
 */
export const getUnifiedInvoices_api = async (filters?: InvoiceFilters): Promise<UserInvoiceResponse> => {
    try {
        const params = new URLSearchParams();
        
        if (filters?.organizationId) {
            params.append('organizationId', filters.organizationId.toString());
        }
        
        if (filters?.brandId) {
            params.append('brandId', filters.brandId.toString());
        }
        
        if (filters?.startDate) {
            params.append('startDate', filters.startDate);
        }
        
        if (filters?.endDate) {
            params.append('endDate', filters.endDate);
        }
        
        if (filters?.minAmount !== undefined) {
            params.append('minAmount', filters.minAmount.toString());
        }
        
        if (filters?.maxAmount !== undefined) {
            params.append('maxAmount', filters.maxAmount.toString());
        }

        const queryString = params.toString();
        const url = `/v2/payments/invoices${queryString ? `?${queryString}` : ''}`;
        
        const response = await Axios.get<UserInvoiceResponse>(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching unified invoices:', error);
        throw error;
    }
};

/**
 * Helper function to format invoice amount with currency
 */
export const formatInvoiceAmount = (amount: number, currency: string): string => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.toUpperCase(),
    }).format(amount);
};

/**
 * Helper function to format invoice date
 */
export const formatInvoiceDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

/**
 * Helper function to get invoice type display name
 */
export const getInvoiceTypeDisplay = (type: string): string => {
    switch (type) {
        case 'subscription':
            return 'Subscription';
        case 'campaign':
            return 'Campaign';
        case 'task-pack':
            return 'Task Pack';
        case 'mixed':
            return 'Subscription + Campaign';
        default:
            return 'Payment';
    }
};

/**
 * Helper function to get invoice status display with color
 */
export const getInvoiceStatusDisplay = (status: string): { label: string; color: string } => {
    switch (status.toLowerCase()) {
        case 'paid':
            return { label: 'Paid', color: '#22C55E' };
        case 'open':
            return { label: 'Open', color: '#F59E0B' };
        case 'draft':
            return { label: 'Draft', color: '#6B7280' };
        case 'void':
            return { label: 'Void', color: '#EF4444' };
        case 'uncollectible':
            return { label: 'Uncollectible', color: '#EF4444' };
        default:
            return { label: status, color: '#6B7280' };
    }
};
